import { useState } from "react";
import { FiEdit3 } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { useCurrentUser } from "../../platform_core/auth/services/AuthService";
import "./PlatformDashboard.scss";

/**
 * Liefert eine tageszeitabhaengige Begruessung.
 *   05:00 - 10:59 -> "Guten Morgen"
 *   11:00 - 17:59 -> "Guten Tag"
 *   18:00 - 22:59 -> "Guten Abend"
 *   23:00 - 04:59 -> "Hallo"
 */
function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return "Guten Morgen";
  if (hour >= 11 && hour < 18) return "Guten Tag";
  if (hour >= 18 && hour < 23) return "Guten Abend";
  return "Hallo";
}

export default function PlatformDashboard() {
  const { data: currentUser } = useCurrentUser(true);
  // Zustand für den Bearbeitungsmodus
  const [isEditing, setIsEditing] = useState(false);

  const displayName = currentUser?.displayName ?? "";
  const firstName = displayName.trim().split(/\s+/)[0] ?? displayName;
  const greeting = getTimeBasedGreeting();

  return (
    <div className="platform-dashboard">
      {/* Begrüßung */}
      <header className="platform-dashboard__header">
        <h1 className="platform-dashboard__title">
          {firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}
        </h1>
        <p className="platform-dashboard__subtitle">
          Hier ist dein Fokus für heute.
        </p>
      </header>

      {/* Aufgaben & Entscheidungen */}
      <section className="platform-dashboard__widget platform-dashboard__widget--full">
        <div className="platform-dashboard__widget-header">
          <span className="platform-dashboard__widget-icon">📋</span>
          <h2 className="platform-dashboard__widget-title">
            DEINE AUFGABEN & ENTSCHEIDUNGEN
          </h2>

          <div className="platform-dashboard__widget-actions">
            {/* Das Drag-Icon - wird nur im Bearbeitungsmodus gezeigt */}
            {isEditing && (
              <div
                className="platform-dashboard__drag-handle"
                title="Widget verschieben"
              >
                <MdDragIndicator aria-hidden="true" size={24} />
              </div>
            )}

            <button
              className="platform-dashboard__widget-edit"
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              title="Bearbeitungsmodus umschalten"
            >
              <FiEdit3 aria-hidden="true" />
              {isEditing ? "Fertig" : "Bearbeiten"}
            </button>
          </div>
        </div>
        <p className="platform-dashboard__widget-description">
          Aus dem Plattformkern. Jeder offene Vorgang zeigt Verantwortung, Status und Frist.
        </p>
        <ul className="platform-dashboard__task-list">
          <li className="platform-dashboard__task platform-dashboard__task--red">
            <span className="platform-dashboard__task-label">HEUTE FÄLLIG:</span>
            <span className="platform-dashboard__task-text">Freigabe Nachtrag</span>
            <span className="platform-dashboard__task-tag">Projekt "Solarpark Süd"</span>
          </li>
          <li className="platform-dashboard__task platform-dashboard__task--orange">
            <span className="platform-dashboard__task-label">MORGEN:</span>
            <span className="platform-dashboard__task-text">
              Ansprechpartner für "Filiale Nord" nachtragen
            </span>
            <span className="platform-dashboard__task-tag">CRM</span>
          </li>
          <li className="platform-dashboard__task platform-dashboard__task--orange">
            <span className="platform-dashboard__task-label">IN 3 TAGEN:</span>
            <span className="platform-dashboard__task-text">
              Marge für "Kalkulation V3" fachlich prüfen
            </span>
            <span className="platform-dashboard__task-tag">Margenkalkulator</span>
          </li>
        </ul>
      </section>

      {/* Projekt-Status + Neueste Kunden */}
      <div className="platform-dashboard__row">
        <section className="platform-dashboard__widget platform-dashboard__widget--half">
          <div className="platform-dashboard__widget-header">
            <span className="platform-dashboard__widget-icon">📈</span>
            <h2 className="platform-dashboard__widget-title">PROJEKT-STATUS</h2>
            <span className="platform-dashboard__widget-source">Cockpit</span>
          </div>
          <ul className="platform-dashboard__project-list">
            <li className="platform-dashboard__project platform-dashboard__project--red">
              <span className="platform-dashboard__project-name">
                Solarpark Süd (Gesperrt)
              </span>
              <span className="platform-dashboard__project-meta">
                Blockade: Netzantwort
              </span>
            </li>
            <li className="platform-dashboard__project platform-dashboard__project--orange">
              <span className="platform-dashboard__project-name">
                Ladeinfrastruktur Campus
              </span>
              <span className="platform-dashboard__project-meta">
                Nächster Schritt: Tiefbau
              </span>
            </li>
          </ul>
        </section>

        <section className="platform-dashboard__widget platform-dashboard__widget--half">
          <div className="platform-dashboard__widget-header">
            <span className="platform-dashboard__widget-icon">👥</span>
            <h2 className="platform-dashboard__widget-title">
              NEUESTE KUNDEN & STANDORTE
            </h2>
            <span className="platform-dashboard__widget-source">CRM</span>
          </div>
          <ul className="platform-dashboard__customer-list">
            <li className="platform-dashboard__customer">
              <span className="platform-dashboard__customer-name">
                Müller GmbH (Gruppe)
              </span>
              <span className="platform-dashboard__customer-meta">
                Status: Daten fehlen
              </span>
            </li>
            <li className="platform-dashboard__customer">
              <span className="platform-dashboard__customer-name">
                Standort: Campus Gebäude B
              </span>
              <span className="platform-dashboard__customer-meta">
                Aktive Projekte: 1
              </span>
            </li>
          </ul>
        </section>
      </div>

      {/* Aktuelle Kalkulationen */}
      <section className="platform-dashboard__widget platform-dashboard__widget--full">
        <div className="platform-dashboard__widget-header">
          <span className="platform-dashboard__widget-icon">∑</span>
          <h2 className="platform-dashboard__widget-title">
            AKTUELLE KALKULATIONEN
          </h2>
          <span className="platform-dashboard__widget-source">Margenkalkulator</span>
        </div>
        <table className="platform-dashboard__calc-table">
          <thead>
            <tr>
              <th>Dokument</th>
              <th>Marge</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Entwurf: Ladesäulen Campus V2</td>
              <td><strong>14%</strong></td>
              <td>Bereit zur Prüfung</td>
            </tr>
            <tr>
              <td>Referenz: Standard PV 100kWp</td>
              <td><strong>12%</strong></td>
              <td className="platform-dashboard__status--ok">✓ Freigegeben</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
